import type LogRocket from 'logrocket';
import { hasPerformanceConsent } from '@aspendental/web-shared-utils';

let initialized = false;

export function resetInitialized() {
	initialized = false;
}

// Dev/QA escape hatch: set localStorage 'loadLogRocket' = 'true' to force
// LogRocket to load outside of prod (e.g. to test GTM tags on staging).
const isForceLoadEnabled = (): boolean => {
	if (typeof window === 'undefined') return false;
	try {
		return window.localStorage.getItem('loadLogRocket') === 'true';
	} catch {
		// localStorage access can throw (private mode, blocked cookies, etc.)
		return false;
	}
};

export const initializeLogRocket = (environment: string, projectId: string): void => {
	if (!projectId || initialized) return;

	// Load in prod normally, or anywhere when the force-load flag is set.
	const forceLoad = isForceLoadEnabled();
	if (!forceLoad && (!environment || environment !== 'prod')) return;

	if (!hasPerformanceConsent()) return;

	// Another app on the same domain may have already initialized LogRocket
	if (typeof window !== 'undefined' && window.LogRocket) {
		initialized = true;
		return;
	}

	initialized = true;

	import('logrocket')
		.then(({ default: LR }) => {
			try {
				LR.init(projectId);
				window.LogRocket = LR;
			} catch {
				// LogRocket init can fail in certain browser environments.
				// Reset flag to allow retry on next navigation.
				initialized = false;
			}
		})
		.catch(() => {
			// Dynamic import can fail due to network issues.
			// Reset flag to allow retry on next navigation.
			initialized = false;
		});
};

declare global {
	interface Window {
		LogRocket: typeof LogRocket;
	}
}
